import { Injectable, inject } from '@angular/core';
import { calculateDistance, calculateIntersection } from '@functions';
import { OverlayColor } from '@models';
import {
  CanvasService,
  DetectionService,
  LogicService,
  MessageService,
  RenderService,
  SimulationService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { PrecisionCuttingDrawService } from './precision-cutting-draw.service';
import { circleInCircleCollision } from './precision-cutting-helpers';
import { PrecisionCuttingMetricsService } from './precision-cutting-metrics.service';

const GLOBAL_CONFIDENCE = 0.1;
const CONFIDENCE_SCISSORS = 0.2;
const CONFIDENCE_CIRCLE_PROCESS = 0.2;
const CONFIDENCE_CUT = 0.2;
const CONFIDENCE_CIRCLE_CUT_FINAL = 0.2;
const DISTANCE_CUT_END = 40;
const DISTANCE_RIGHT_TOOL_END = 40;
const RATIO_SIZE_ELLIPSE_TO_DETECTION = 0.9;
const THRESHOLD_END_POINT_RIGHT_CUT = 80;
const THRESHOLD_DISTANCE_CHUNK_MAIN_CIRCLE = 50;
const MAX_BRIDGE_SIZE = 65;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private canvasService = inject(CanvasService);
  private detectionService = inject(DetectionService);
  private logicService = inject(LogicService);
  private messageService = inject(MessageService);
  private precisionCuttingMetricsService = inject(
    PrecisionCuttingMetricsService,
  );
  private precisionCuttingDrawService = inject(PrecisionCuttingDrawService);
  private renderService = inject(RenderService);
  private simulationService = inject(SimulationService);

  cycleWorkerId: string = undefined;
  taskWorkerId: string = undefined;
  cutCheckWorkerId: string = undefined;

  cutsOccurences: number[] = [];
  endOfSecondCutCoordinates = { x: 0, y: 0 };

  bridgeSize: number = null;

  checkSample(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService.checkSamplePosition({
          sample: 'circle',
          workerId: this.cycleWorkerId,
          confidence: 0.5,
          width: 640,
          height: 400,
          moveX: -100,
          moveY: 100,
          image: 'overlays/circle-gauze.svg',
        });
        resolve(this.simulationService.state() === 'simulation');
      } catch (error) {
        reject(error);
      }
    });
  }

  checkInstruments(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService
          .checkInstrumentsPosition([
            {
              id: 'grasper',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: -350,
              radius: 90,
              image: 'overlays/instrument-left.svg',
            },
            {
              id: 'scissors',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: 170,
              radius: 90,
              image: 'overlays/instrument-right.svg',
            },
          ])
          .then((instrumentsConfirmed) => {
            if (!instrumentsConfirmed) {
              this.messageService.showDetectionMessage({
                text: 'Bring your instruments together in the camera view',
                identifier: 'checkInstruments',
              });
            }
            resolve(instrumentsConfirmed);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  firstCut(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const [circle, scissors, cut] = this.detectionService.getBoxes(
          ['circle', 'scissors', 'cut'],
          this.cycleWorkerId,
        );

        if (
          circle &&
          circle.confidence > CONFIDENCE_CIRCLE_PROCESS &&
          circle.max.x - circle.min.x < 600
        ) {
          this.drawCutOverlay(
            (240 / 180) * Math.PI,
            (390 / 180) * Math.PI,
            'cw',
            true,
          );
        }

        const distScissorsEndCutPath = scissors
          ? calculateDistance(
              { x: scissors.center.x, y: scissors.center.y },
              {
                x: this.precisionCuttingDrawService.endPointsCircle[0],
                y: this.precisionCuttingDrawService.endPointsCircle[1],
              },
            )
          : null;

        const distCutEnd = cut
          ? calculateDistance(
              { x: cut.center.x, y: cut.center.y },
              {
                x: this.precisionCuttingDrawService.endPointsCircle[0],
                y: this.precisionCuttingDrawService.endPointsCircle[1],
              },
            )
          : null;

        if (circle && distCutEnd !== null && distCutEnd < 120) {
          this.renderService.addStatic({
            id: 'rotatescissorsLeft',
            overlay: {
              type: 'image',
              image: 'overlays/rotate-scissors.svg',
              x: circle.center.x - 200,
              y: circle.center.y,
            },
            timeout: 3000,
          });
        }

        const conditionCut = cut
          ? cut.confidence > CONFIDENCE_CUT && distCutEnd < DISTANCE_CUT_END
          : null;

        const isFirstCutDone = scissors
          ? (distScissorsEndCutPath < DISTANCE_RIGHT_TOOL_END &&
              scissors.confidence > CONFIDENCE_SCISSORS) ||
            conditionCut
          : null;

        if (isFirstCutDone) {
          this.calculateTargetPointsEllipse((390 / 180) * Math.PI);
          this.messageService.showDetectionMessage({
            text: 'Cut the black circle along the highlighted path',
            identifier: 'firstCut',
          });
        }
        resolve(isFirstCutDone);
      } catch (error) {
        reject(error);
      }
    });
  }

  secondCut(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const bridge = this.detectionService.getBox(
          'bridge',
          this.cycleWorkerId,
        );

        this.drawCutOverlay(
          (600 / 180) * Math.PI,
          (390 / 180) * Math.PI,
          'ccw',
        );

        this.drawIconScissorsTurnInSecondCut();

        const bridgeWidth = bridge?.max.x - bridge?.min.x;

        this.bridgeSize =
          this.bridgeSize > bridgeWidth || !this.bridgeSize
            ? bridgeWidth
            : this.bridgeSize;

        const bridgesAbsent =
          this.detectionService.getNumberOfBoxEmptys(
            'bridge',
            this.cycleWorkerId,
            5,
          ) === 5;

        const cutAbsent =
          this.detectionService.getNumberOfBoxEmptys(
            'cut',
            this.cycleWorkerId,
            5,
          ) === 5;

        const isCutDone = this.detectionService.getRegressionTrend(
          'circleIsCut',
          this.cutCheckWorkerId,
          5,
        );
        const isCutNotDone = this.detectionService.getRegressionTrend(
          'circleNotCut',
          this.cutCheckWorkerId,
          5,
        );

        const isSecondCutDone =
          this.bridgeSize < MAX_BRIDGE_SIZE &&
          cutAbsent &&
          bridgesAbsent &&
          isCutDone &&
          !isCutNotDone;

        resolve(isSecondCutDone);
      } catch (error) {
        reject(error);
      }
    });
  }

  calculateTargetPointsEllipse(endAngle: number) {
    let rotation = 0;

    const circle = this.detectionService.getBox('circle', this.cycleWorkerId);

    const circleWidth =
      RATIO_SIZE_ELLIPSE_TO_DETECTION * (circle.max.x - circle.min.x);
    const circleHeight =
      RATIO_SIZE_ELLIPSE_TO_DETECTION * (circle.max.y - circle.min.y);
    const circleCenter = [circle.center.x, circle.center.y];

    const a = circleWidth / 2;
    const b = circleHeight / 2;
    rotation = (rotation / 180) * Math.PI;
    const xEnd =
      circleCenter[0] -
      a * Math.cos(endAngle) * Math.cos(rotation) +
      b * Math.sin(endAngle) * Math.sin(rotation);
    const yEnd =
      circleCenter[1] -
      a * Math.cos(endAngle) * Math.sin(rotation) -
      b * Math.sin(endAngle) * Math.cos(rotation);
    this.endOfSecondCutCoordinates = { x: xEnd, y: yEnd };
  }

  drawCutOverlay(
    startAngle: number,
    endAngle: number,
    direction: 'cw' | 'ccw' = 'cw',
    start = false,
  ) {
    const circle = this.detectionService.getBox('circle', this.cycleWorkerId);

    const circleWidth =
      RATIO_SIZE_ELLIPSE_TO_DETECTION * (circle?.max.x - circle?.min.x);

    const circleHeight =
      RATIO_SIZE_ELLIPSE_TO_DETECTION * (circle?.max.y - circle?.min.y);

    if (circle) {
      this.precisionCuttingDrawService.drawEllipsePart(
        circleWidth,
        circleHeight,
        [circle.center.x, circle.center.y],
        startAngle,
        endAngle,
        direction,
      );
      if (start) {
        this.precisionCuttingDrawService.drawCutStart(
          circleWidth,
          circleHeight,
          [circle.center.x, circle.center.y],
          startAngle,
        );
      }
    }
  }

  drawIconScissorsTurnInSecondCut() {
    const [scissors, circle, cut] = this.detectionService.getBoxes(
      ['scissors', 'circle', 'cut'],
      this.cycleWorkerId,
    );

    const rightCut = cut
      ? this.detectionService
          .getSortedBoxesHorizontally('cut', this.cycleWorkerId)
          .at(-1)
      : null;

    const distCutPointOfTurn =
      rightCut && circle
        ? calculateDistance(
            { x: rightCut.center.x, y: rightCut.center.y },
            {
              x: circle.max.x,
              y: circle.center.y,
            },
          )
        : null;

    const distScissorsPointOfTurn =
      scissors && circle
        ? calculateDistance(
            { x: scissors.center.x, y: scissors.center.y },
            {
              x: circle.max.x,
              y: circle.center.y,
            },
          )
        : null;

    if (
      circle &&
      distCutPointOfTurn !== null &&
      distCutPointOfTurn < THRESHOLD_END_POINT_RIGHT_CUT &&
      distScissorsPointOfTurn !== null &&
      distScissorsPointOfTurn < THRESHOLD_END_POINT_RIGHT_CUT
    ) {
      this.renderService.addStatic({
        id: 'rotateScissorsRight',
        overlay: {
          type: 'image',
          image: 'overlays/rotate-scissors.svg',
          x: circle.max.x + 50,
          y: circle.center.y,
        },
        timeout: 3000,
      });
    }
  }

  positionCut(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const circleCut = this.detectionService.getBox(
          'circleCut',
          this.cycleWorkerId,
        );

        const target = {
          ...this.canvasService.convertCenterCoordinates({
            moveX: -400,
            moveY: 150,
          }),
          radius: 160,
        };

        const isCutCircleOnPosition = circleCut
          ? circleCut.confidence > CONFIDENCE_CIRCLE_CUT_FINAL &&
            circleInCircleCollision(
              circleCut.center.x,
              circleCut.center.y,
              target.x,
              target.y,
              target.radius,
            )
          : null;

        if (!isCutCircleOnPosition) {
          this.renderService.addStatic({
            id: 'cutCircle',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.blue,
              transparency: 0.3,
              image: 'overlays/cut-circle.svg',
              ...target,
            },
          });
        } else {
          this.renderService.addStatic({
            id: 'cutCircle',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              image: 'overlays/check.svg',
              ...target,
            },
            replace: { id: 'cutCircle' },
            timeout: 2000,
          });
        }
        resolve(isCutCircleOnPosition);
      } catch (error) {
        reject(error);
      }
    });
  }

  updateHistoryOfCuts() {
    let numberCutsNotCounted = 0;
    const cuts = this.detectionService.getAllBoxInstances(
      'cut',
      this.cycleWorkerId,
    );
    const [circle, circleCut] = this.detectionService.getBoxes(
      ['circle', 'circleCut'],
      this.cycleWorkerId,
    );

    const distanceChunkMainCircle =
      circleCut && circle
        ? calculateDistance(
            { x: circleCut.center.x, y: circleCut.center.y },
            { x: circle.center.x, y: circle.center.y },
          )
        : null;

    cuts.forEach((cut) => {
      if (cut) {
        const iouCut =
          cut && circleCut ? calculateIntersection(circleCut, cut) : null;
        if (
          circleCut && circle && cut
            ? circleCut.confidence > GLOBAL_CONFIDENCE &&
              circle.confidence > GLOBAL_CONFIDENCE &&
              distanceChunkMainCircle > THRESHOLD_DISTANCE_CHUNK_MAIN_CIRCLE &&
              iouCut == 0
            : null
        ) {
          cuts[numberCutsNotCounted] = null;
          numberCutsNotCounted += 1;
        }
      }
    });
  }

  toolTracking() {
    this.precisionCuttingMetricsService.trackGrasper(
      this.detectionService.getBox('grasper', this.cycleWorkerId),
    );
    this.precisionCuttingMetricsService.trackScissors(
      this.detectionService.getBox('scissors', this.cycleWorkerId),
    );
  }
}
